import { useRef } from "react";
import { useInView } from "framer-motion";
import { Flame, ExternalLink } from "lucide-react";
import { T } from "@/constants/testIds";
import { LINKS, DEAD_WALLET } from "@/constants/links";
import { useCountUp } from "@/hooks/useStats";
import { fmtCompact, fmtPct } from "@/lib/format";

export default function BurnCard({ stats }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  const pct = useCountUp(stats?.burn_percent, inView, 1800);
  const amount = useCountUp(stats?.burn_amount, inView, 1800);
  const total = stats?.token?.total_supply;

  return (
    <div
      ref={ref}
      data-testid={T.burnCard}
      className="card relative p-7 md:p-10 h-full flex flex-col justify-between overflow-hidden !bg-[#121410]"
    >
      <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[color:var(--hot)]/20 blur-3xl pointer-events-none" />
      <div className="absolute -left-10 -bottom-10 w-56 h-56 rounded-full bg-[color:var(--lime)]/15 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-center gap-3 mb-6">
          <span className="sticker sticker-pink !text-[0.62rem]">
            <Flame size={12} /> The dead wallet
          </span>
          <span className="kicker text-[color:var(--muted)]">verified onchain · balanceOf(0x…dEaD)</span>
        </div>

        <div className="kicker text-paper/60 mb-3">Supply burned forever</div>
        <div
          data-testid={T.burnPercent}
          className="font-display tabular text-[18vw] sm:text-[13vw] lg:text-[6.8vw] xl:text-[6vw] leading-[0.9] text-lime"
        >
          {pct == null ? "—" : fmtPct(pct)}
        </div>

        <div className="mt-6 burn-bar">
          <span style={{ width: `${inView && stats?.burn_percent ? Math.min(100, stats.burn_percent) : 0}%` }} />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4">
          <div>
            <div className="kicker text-[color:var(--muted)] mb-1">Tokens sent to the void</div>
            <div data-testid={T.burnAmount} className="font-display tabular text-2xl md:text-3xl text-paper">
              {amount == null ? "—" : fmtCompact(amount)}
            </div>
          </div>
          <div>
            <div className="kicker text-[color:var(--muted)] mb-1">Total supply</div>
            <div className="font-display tabular text-2xl md:text-3xl text-paper">
              {total == null ? "—" : fmtCompact(total)}
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-8 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-white/10">
        <span className="font-mono text-[11px] text-paper/60 break-all">{DEAD_WALLET}</span>
        <a
          href={LINKS.deadWallet}
          target="_blank"
          rel="noreferrer"
          data-testid={T.burnLink}
          className="btn btn-outline-lime btn-sm"
        >
          Inspect the void <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}
